import { FC, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { ApexLineChart, ApexPieChart } from '../../../../../PDFViewer/components/ApexChart';
import styles from './styles';
import { AppContext } from '../../../../../../context';
import moment from 'moment';
import 'moment/locale/de';
import ApexCharts from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { SectionHeaders, SECTION_HEADER_DEFAULTS } from '../PdfConfig';
import Dropzone from 'react-dropzone';
import { uploadImage } from '../../../../../../apis/cdn.apis';
import { uploadErrorMessage } from '../../../../../../utils/uploadLimits';
import html2canvas from 'html2canvas';
import { client } from '../../../../../../App';
import { GET_AUTOMATED_COLUMNS, GET_AUTOMATED_BAR_CHART } from '../../../../../../queries/widgets';
import { formatBarChartPeriodLabel, buildRollingPeriodLabels } from '../../../../../../utils/formatBarChartPeriodLabel';

/** Wait for ApexCharts to finish painting before dataURI capture (preview / publish). */
export const CHART_CAPTURE_RENDER_DELAY_MS = 600;

interface FactsheetPreviewProps {
  data?: any;
  title?: string;
  subtitle?: string;
  isPreviewStarted?: boolean;
  onChart1ImageChange?: (dataUrl?: string) => void;
  onChart2ImageChange?: (dataUrl?: string) => void;
  onChart3ImageChange?: (dataUrl?: string) => void;
  onPieChart1ImageChange?: (dataUrl?: string) => void;
  onPieChart2ImageChange?: (dataUrl?: string) => void;
  onPieChart3ImageChange?: (dataUrl?: string) => void;
  onPieChartImagesChange?: (countryChartUrl: string | undefined, sectorChartUrl: string | undefined) => void;
  onResetPreviewStarted: (value: boolean) => void;
  onDataChange?: (data: any) => void;
  /** Parent calls this before preview/publish PDF to capture fully rendered charts. */
  onRegisterChartCapture?: (capture: () => Promise<void>) => void;
}

export const FactsheetPreview: FC<FactsheetPreviewProps> = ({
  data,
  title = 'Active Mining I',
  subtitle = 'Das Anlageuniversum des Fonds sind Rohstoffaktien aus dem Bereich Erzgewinnung und Verhüttung.',
  isPreviewStarted,
  onChart1ImageChange,
  onChart2ImageChange,
  onChart3ImageChange,
  onPieChart1ImageChange,
  onPieChart2ImageChange,
  onPieChart3ImageChange,
  onResetPreviewStarted,
  onDataChange,
  onRegisterChartCapture,
}) => {
  const { document } = useContext(AppContext);
  const effectiveData = data || {};
  const chartImageUrl = effectiveData.chartImageUrl;
  const awardsImageUrl = effectiveData.awardsImageUrl;
  const effectiveTitle = effectiveData.title || title;
  const effectiveSubtitle = effectiveData.subtitle || subtitle;
  const effectiveDate = effectiveData.published_at || 'November 2025';
  const formattedDate = moment(effectiveDate, ['YYYY-MM-DD', 'YYYY-MM']).locale('de').format('MMMM YYYY').toUpperCase();
  const sectionHeaders: SectionHeaders = {
    ...SECTION_HEADER_DEFAULTS,
    ...(effectiveData.sectionHeaders || {}),
  };

  // Master Data (Stammdaten)
  const masterData = effectiveData.masterData || {
    fondskategorie: 'Aktienfonds',
    auflagedatum: '02.09.2024',
    wkn: 'A3EWBE',
    isin: 'DE000A3EWBE8',
    erstausgabepreis: '148,37 EUR',
    fondsvolumen: '9,96 Mio. EUR',
    kursNav: '146,39 EUR',
    fondswaehrung: 'EUR',
    fondsdomizil: 'DE',
    geschaeftsjahresende: '31-Aug.',
    ertragsverwendung: 'Thesaurierend',
    rechtsform: 'OGAW-Sondervermögen',
    risikoErtragsprofil: '4',
    sparplanfaehig: 'ja',
    mindestzeichnungbetrag: '100.000 EUR',
  };

  // Fees (Gebühren)
  const fees = effectiveData.fees || {
    ausgabeaufschlag: '0% (MAX 5%)',
    laufendeKostenTer: '1,94%',
    davonVerwaltungsgebuehr: '1,23% (MAX 2%)',
  };

  // Fund Manager Info
  const fundManager = effectiveData.fundManager || {
    name: 'Kai Heinrich',
    company: 'Plutos Vermögensverwaltung',
    research: 'Research & Strategie: Markus Mezger',
    company2: 'Micro meets Macro GmbH',
    phone: '+49 61 28 95 23 0',
    hedgingRatio: '35%',
    photoUrl: undefined,
  };

  // Monthly Commentary — full text in one card (PDF uses react-pdf wrap; no word split).
  const monthlyCommentary =
    typeof effectiveData.monthlyCommentary === 'string' ? effectiveData.monthlyCommentary.trim() : '';
  const commentaryParagraphs = monthlyCommentary
    ? monthlyCommentary.split(/\n\n+/).map((p: string) => p.trim()).filter(Boolean)
    : [];

  // Chart data
  const lineChartData = effectiveData.lineChartData || [];
  const performanceNotice: string =
    typeof effectiveData.performanceNotice === 'string' ? effectiveData.performanceNotice.trim() : '';
  const historyLegendLabel: string = effectiveData.historyLegendLabel || effectiveTitle;
  const historyCurrencySymbol: string = effectiveData.historyCurrencySymbol || '€';
  
  // Indexed value development chart data (for percentage display)
  const indexedChartData = effectiveData.indexedChartData || lineChartData;

  // Get cumulative performance data - support both old and new structure
  const getCumulativePerformanceData = () => {
    const perf = effectiveData.cumulativePerformance;
    // New V1-style structure (labels/values/columns)
    if (perf && perf.labels && Array.isArray(perf.labels) && perf.values && Array.isArray(perf.values)) {
      return {
        labels: perf.labels,
        values: perf.values,
        columns: perf.columns || [],
      };
    }
    // Old key-value structure - migrate on the fly
    if (perf && typeof perf === 'object' && !perf.labels) {
      return {
        labels: ['1 Monat', '6 Monate', '1 Jahr', '3 Jahre', '5 Jahre', 'Lfd. Jahr', 'seit Auflage'],
        values: [[
          perf.oneMonth || '+1,76',
          perf.sixMonths || '-3,04',
          perf.oneYear || '-2,54',
          perf.threeYears || '-1,07',
          perf.fiveYears || '-0,68',
          perf.currentYear || '+0,95',
          perf.sinceInception || '+95,38'
        ]],
        columns: [],
      };
    }
    // Default
    return {
      labels: ['1 Monat', '6 Monate', '1 Jahr', '3 Jahre', '5 Jahre', 'Lfd. Jahr', 'seit Auflage'],
      values: [['+1,76', '-3,04', '-2,54', '-1,07', '-0,68', '+0,95', '+95,38']],
      columns: [],
    };
  };
  const cumulativePerformanceData = getCumulativePerformanceData();

  // State for fetched dynamic cell data
  const [cellDataCache, setCellDataCache] = useState<Map<string, any>>(new Map());

  // Get ISIN for GraphQL queries
  const isin = document?.template?.isins?.[0] || effectiveData?.historyIsin || '';

  // Helper function to format date to YYYY-MM-DD for until parameter
  const formatDateToUntil = useCallback((dateStr: string | undefined): string | undefined => {
    if (!dateStr) return undefined;
    // If already YYYY-MM-DD, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // Try to parse and format
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Get until date from published_at
  const until = formatDateToUntil(effectiveData?.published_at);

  // Clear cache when until changes
  useEffect(() => {
    setCellDataCache(new Map());
  }, [until]);

  // Fetch dynamic cell data using GraphQL
  const fetchCellData = useCallback(async (cell: any, rowIndex: number, colIndex: number) => {
    if (!isin || !cell?.isDynamic || !cell?.fields?.length) return null;
    
    const cellKey = `cumulative-${rowIndex}-${colIndex}-${until || ''}`;
    
    // Check cache first
    if (cellDataCache.has(cellKey)) {
      return cellDataCache.get(cellKey);
    }

    try {
      const res = await client.query({
        query: GET_AUTOMATED_COLUMNS,
        variables: { param: { data: [cell], isin, until } },
      });
      
      const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
      
      // Cache the result - create a new Map to trigger re-render
      setCellDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(cellKey, fetchedValue);
        return newCache;
      });
      
      return fetchedValue;
    } catch (error) {
      console.error('Failed to fetch cell data:', error);
      return null;
    }
  }, [isin, until, cellDataCache]);

  // Fetch all dynamic cells when cumulative performance data changes
  useEffect(() => {
    if (!isin || !cumulativePerformanceData.values || !Array.isArray(cumulativePerformanceData.values)) return;
    
    const fetchAllCells = async () => {
      const promises: Promise<void>[] = [];
      cumulativePerformanceData.values.forEach((row: any[], rowIndex: number) => {
        row.forEach((cell: any, colIndex: number) => {
          if (cell?.isDynamic && cell?.fields?.length) {
            promises.push(
              fetchCellData(cell, rowIndex, colIndex).then(() => undefined)
            );
          }
        });
      });
      await Promise.all(promises);
    };
    
    fetchAllCells();
  }, [isin, cumulativePerformanceData.values, fetchCellData, until]);

  // Fetch dynamic value data using GraphQL
  const fetchDynamicValue = useCallback(async (section: string, field: string, value: any) => {
    if (!isin || !value?.isDynamic || !value?.fields?.length) return null;
    
    const valueKey = `${section}.${field}-${until || ''}`;
    
    // Check cache first
    if (cellDataCache.has(valueKey)) {
      return cellDataCache.get(valueKey);
    }

    try {
      const res = await client.query({
        query: GET_AUTOMATED_COLUMNS,
        variables: { param: { data: [value], isin, until } },
      });
      
      const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
      
      // Cache the result
      setCellDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(valueKey, fetchedValue);
        return newCache;
      });
      
      return fetchedValue;
    } catch (error) {
      console.error('Failed to fetch dynamic value:', error);
      return null;
    }
  }, [isin, until, cellDataCache]);

  // Helper function to get display value (fetched or static)
  const getDisplayValue = useCallback((section: string, field: string, defaultValue: string): string => {
    const value = effectiveData[section]?.[field];
    
    // If it's a dynamic value, try to get from cache
    if (typeof value === 'object' && value?.isDynamic && value?.fields?.length) {
      const valueKey = `${section}.${field}-${until || ''}`;
      const fetchedValue = cellDataCache.get(valueKey);
      if (fetchedValue !== undefined && fetchedValue !== null) {
        return String(fetchedValue);
      }
      // If not in cache yet, return default while loading (ensure it's a string)
      return typeof defaultValue === 'string' ? defaultValue : '';
    }
    
    // Return static value or default (ensure it's a string)
    if (typeof value === 'string') {
      return value;
    }
    return typeof defaultValue === 'string' ? defaultValue : '';
  }, [effectiveData, cellDataCache, until]);

  // Fetch all dynamic values when data changes
  useEffect(() => {
    if (!isin) return;
    
    const fetchAllDynamicValues = async () => {
      const promises: Promise<void>[] = [];
      
      // Fetch masterData dynamic values
      ['fondskategorie', 'auflagedatum', 'wkn', 'isin', 'erstausgabepreis', 'fondsvolumen', 
       'kursNav', 'fondswaehrung', 'fondsdomizil', 'geschaeftsjahresende', 'ertragsverwendung', 
       'rechtsform', 'risikoErtragsprofil', 'sparplanfaehig', 'mindestzeichnungbetrag'].forEach(field => {
        const value = effectiveData.masterData?.[field];
        if (value?.isDynamic && value?.fields?.length) {
          promises.push(
            fetchDynamicValue('masterData', field, value).then(() => undefined)
          );
        }
      });
      
      // Fetch fees dynamic values
      ['ausgabeaufschlag', 'laufendeKostenTer', 'davonVerwaltungsgebuehr'].forEach(field => {
        const value = effectiveData.fees?.[field];
        if (value?.isDynamic && value?.fields?.length) {
          promises.push(
            fetchDynamicValue('fees', field, value).then(() => undefined)
          );
        }
      });
      
      await Promise.all(promises);
    };
    
    fetchAllDynamicValues();
  }, [isin, effectiveData.masterData, effectiveData.fees, fetchDynamicValue, until]);

  // Rolling Performance Data
  const rollingPerformance = effectiveData.rollingPerformance || {
    periods: [
      { label: '08.09.2020 bis 08.09.2021', netto: 28.63, brutto: 35.40 },
      { label: '08.09.2021 bis 08.09.2022', netto: -13.13, brutto: -13.13 },
      { label: '08.09.2022 bis 08.09.2023', netto: 7.59, brutto: 7.59 },
      { label: '08.09.2023 bis 09.09.2024', netto: 7.36, brutto: 7.36 },
      { label: '09.09.2024 bis 09.09.2025', netto: 13.06, brutto: 13.06 },
    ],
  };

  // State for automated bar chart data
  const [automatedPeriodsData, setAutomatedPeriodsData] = useState<any[]>([{ name: '', data: [] }]);
  const [automatedPeriodLabels, setAutomatedPeriodLabels] = useState<string[]>([]);

  // Fetch automated bar chart data
  const fetchBarChart = useCallback(async () => {
    const rollingIsin = rollingPerformance?.isin || isin;
    const automationType = rollingPerformance?.automationType;
    
    if (!rollingIsin || rollingPerformance?.chartType !== 'Automated') {
      return;
    }

    try {
      const result = await client.query({
        query: GET_AUTOMATED_BAR_CHART,
        variables: { isin: rollingIsin, type: automationType, until },
      });

      const data = result.data.getAutomatedBarChart;

      if (automationType === 'all') {
        // Group data by type
        const groupedData = data.reduce((acc: any, item: any) => {
          if (!acc[item.type]) {
            acc[item.type] = [];
          }
          acc[item.type].push(item);
          return acc;
        }, {});

        const mainColors = rollingPerformance?.barColorSets || document?.template?.colorSets?.slice(0, 2).reverse() || ['#003F7A', '#667a7a'];

        const chartSeries = Object.keys(groupedData)
          .sort((a, b) => {
            if (a === 'gross_perf_year_as_of_today') return -1;
            if (b === 'gross_perf_year_as_of_today') return 1;
            return a.localeCompare(b);
          })
          .map((type, index) => ({
            name: (rollingPerformance?.legend && rollingPerformance.legend[type])
              ? rollingPerformance.legend[type]
              : type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
            data: groupedData[type].map((item: any) => item.value),
            color: Object.keys(groupedData).length === 2
              ? mainColors[index]
              : document?.template?.colorSets?.[index] || mainColors[index],
          }));

        const grossRows = (groupedData['gross_perf_year_as_of_today'] || []).sort(
          (a: any, b: any) =>
            moment(a.end, 'DD.MM.YYYY', true).valueOf() -
            moment(b.end, 'DD.MM.YYYY', true).valueOf(),
        );
        const uniquePeriods = buildRollingPeriodLabels(grossRows);
        setAutomatedPeriodLabels(uniquePeriods);
        setAutomatedPeriodsData(chartSeries);
      } else {
        const labelRows = [...data].sort(
          (a: any, b: any) =>
            moment(a.end, 'DD.MM.YYYY', true).valueOf() -
            moment(b.end, 'DD.MM.YYYY', true).valueOf(),
        );
        setAutomatedPeriodLabels(buildRollingPeriodLabels(labelRows));
        setAutomatedPeriodsData([{ name: '', data: labelRows.map((item: any) => item.value) }]);
      }
    } catch (error) {
      console.error('Failed to fetch automated bar chart data:', error);
    }
  }, [rollingPerformance, isin, until, document?.template?.colorSets]);

  // Fetch automated bar chart data when chartType is Automated
  useEffect(() => {
    if (rollingPerformance?.chartType === 'Automated') {
      fetchBarChart();
    }
  }, [rollingPerformance?.chartType, rollingPerformance?.isin, rollingPerformance?.automationType, fetchBarChart, until]);

  // Investment Strategy
  const investmentStrategy = effectiveData.investmentStrategy || 'Der Active Mining Fund ist ein offensiver Mischfonds mit dem Fokus auf globale Aktien aus dem Bergbau- und Rohstoffsektor. Seit dem 01.01.2019 hat der Fonds ein passives Anlagekonzept mit dem Ziel einer Überinvestition des Fonds mit dividendenstarken Aktien, die sich durch ein positives Momentum auszeichnen. Das Fondsmanagement beachtet bei der Auswahl der Investments fundamentale Kennzahlen, ergänzt um das technische Bild und Behavioural Finance-Aspekte vor allem für Anleger, die eine hohe Partizipation am Aktienmarkt mit soliden Basiswerten sowie einem aktiven Management wünschen und eine entsprechende Risikobereitschaft mitbringen.';

  // Top 10 Stock Positions
  const top10Stocks = effectiveData.top10Stocks || [
    { name: 'Reg Shs Tudor Gold Corp', percentage: 3.44 },
    { name: 'SBI Holdings Inc', percentage: 2.65 },
    { name: 'Intesa Sanpaolo S.p.A.', percentage: 2.46 },
    { name: 'ABN AMRO Group N.V. Shs Depositar...', percentage: 2.41 },
    { name: 'Glb X Silv USD', percentage: 2.35 },
    { name: 'Italgas SpA', percentage: 2.34 },
    { name: 'Nexstar Media Group Inc', percentage: 2.34 },
    { name: 'SUMITOMO MITSUI FIN.', percentage: 2.30 },
    { name: 'Oji Paper Co Ltd', percentage: 2.30 },
    { name: 'Haseko Corp', percentage: 2.27 },
  ];

  // Get colorSets from template
  const colorSets = document?.template?.colorSets || [];
  const defaultColors = ['#1c4f92', '#62418b', '#853874', '#9d345c', '#a63f43', '#a0512c'];

  const formatStockPercentage = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return '';
    
    // If it's a string, preserve it as-is (removing % if present)
    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized.endsWith('%') ? normalized.slice(0, -1) : normalized;
    }
    
    // If it's a number, format to preserve trailing zeros
    const numValue = Number(value);
    if (isNaN(numValue)) return '';
    
    // Convert to string to check original precision
    const strValue = String(value);
    const decimalIndex = strValue.indexOf('.');
    
    if (decimalIndex === -1) {
      // No decimal point, format with 2 decimal places for consistency
      return numValue.toFixed(2);
    }
    
    // Has decimal point - determine precision from original string representation
    const decimalPart = strValue.substring(decimalIndex + 1);
    const precision = decimalPart.length;
    
    // Format with the original precision, but ensure at least 2 decimal places
    // This preserves values like 4.601 (3 decimals) but ensures 5.1 becomes 5.10
    const minPrecision = Math.max(precision, 2);
    return numValue.toFixed(minPrecision);
  };

  // Sector Allocation
  const sectorAllocation = useMemo(() => effectiveData.sectorAllocation || [
    { label: 'Finanzwesen', value: 27.6 },
    { label: 'Industrie', value: 18.2 },
    { label: 'Nicht-Basiskonsumgüter', value: 9.7 },
    { label: 'Basisgüter', value: 9.5 },
    { label: 'Hilfs- & Betriebsstoffe', value: 7.7 },
    { label: 'Sonstige', value: 27.3 },
  ], [effectiveData.sectorAllocation]);

  // Currency Allocation
  const currencyAllocation = useMemo(() => effectiveData.currencyAllocation || [
    { label: 'JPY', value: 38.7 },
    { label: 'USD', value: 9.4 },
    { label: 'CAD', value: 9.7 },
    { label: 'EUR', value: 35.4 },
    { label: 'CHF', value: 6.2 },
    { label: 'Sonstige', value: 0.6 },
  ], [effectiveData.currencyAllocation]);

  // Asset Allocation
  const assetAllocation = useMemo(() => effectiveData.assetAllocation || [
    { label: 'Aktien', value: 96.5 },
    { label: 'Kasse', value: 37.2 },
    { label: 'Übrige Fonds', value: 1.9 },
    { label: 'Derivate', value: -35.6 },
  ], [effectiveData.assetAllocation]);

  // Target Group, Investment Horizon, Risk Indicator
  const targetGroup = effectiveData.targetGroup || 'Der Teilfonds eignet sich für aktienorientierte Anleger, die in Investmentfonds ein geeignetes Mittel zur Teilnahme an der Kapitalmarktentwicklung sehen. Er eignet sich ferner für den eher erfahrenen Anleger, der damit bestimmte Anlageziele verfolgt. Erfahrung mit Kapitalmarktprodukten ist nicht erforderlich. Das Anlageziel des Teilfonds ist ausgerichtet auf die Vermögensbildung. In einem Anleger-Portfolio kann er die Rolle eines Basisinvestments einnehmen.';

  const investmentHorizon = effectiveData.investmentHorizon || 'Der Anleger in diesem Teilfonds sollte eine Anlage von drei bis fünf Jahren planen, da der Anleger in der Lage sein muss, Verluste hinzunehmen.';

  // 1-7 scale; the mapper defaults to 0 = unknown (no box highlighted) instead of inventing a value.
  const riskIndicator = effectiveData.riskIndicator || 0;

  // Opportunities and Risks
  const opportunities = effectiveData.opportunities || [
    'Aktien bieten langfristig ein überdurchschnittliches Renditepotential.',
    'Die internationale Ausrichtung ermöglicht eine breite Streuung der Investments.',
    'Das aktive Portfoliomanagement und die flexible Ausrichtung bieten ein attraktives Chancen-/Risikoprofil.',
    'Der Einsatz von Derivaten kann helfen, das Portfolio bei negativen Marktentwicklungen abzusichern.',
    'Durch Hebelwirkungen können überproportionale Wertsteigerungen erreicht werden.',
  ];

  const risks = effectiveData.risks || [
    'Die Werte von Aktien können stark schwanken und es sind deutliche Verluste möglich.',
    'Durch die breite Streuung der Investments kann es zu einer begrenzten Teilnahme an der Wertentwicklung einzelner Regionen kommen.',
    'Der Einsatz von Derivaten kann zu erheblichen Verlusten im Portfolio führen.',
    'Die Geldpolitik der Notenbanken kann den Markt unerwartet beeinflussen. Fehlentscheidungen bei Auswahl und Zeitpunkt der Investitionen können nicht ausgeschlossen werden.',
  ];

  // Sales Prospectus
  const salesProspectus = effectiveData.salesProspectus || 'Die vollständigen Angaben zu dem Fonds, einschließlich den mit einer Fondsanlage verbundenen Chancen und Risiken sind dem jeweils aktuellen Verkaufsprospekt nebst Verwaltungsreglement oder Satzung sowie ggf. dem Basisinformationsblatt (PRIIP-KIDs), ergänzt durch den letzten geprüften Jahresbericht bzw. den letzten Halbjahresbericht zu entnehmen. Diese Unterlagen stellen die alleinverbindliche Grundlage für den Kauf von Fondsanteilen dar.';

  // Legal Notes
  const legalNotesData = effectiveData.legalNotes || {
    verwahrstelle: 'Kreissparkasse Köln',
    verwaltungsgesellschaft: 'Monega Kapitalanlagegesellschaft mbH',
    text: 'Dies ist eine Werbemitteilung. Die in diesem Factsheet angegebenen Fondsinformationen sind zu allgemeinen Informationszwecken erstellt worden und dienen einer ersten Orientierung zu dem Produkt. Sie ersetzen weder eigene Marktrecherchen noch sonstige rechtliche, steuerliche oder finanzielle Informationen oder Beratung. Das Factsheet stellt keine Kauf- oder Verkaufsaufforderung oder Anlageberatung dar. Das Factsheet enthält nicht alle für wirtschaftlich bedeutende Entscheidungen wesentlichen Angaben und kann von Informationen und Einschätzungen anderer Quellen oder Marktteilnehmer abweichen.\n\nDie vollständigen Angaben zu dem Fonds, einschließlich den mit einer Fondsanlage verbundenen Chancen und Risiken sind dem jeweils aktuellen Verkaufsprospekt nebst Verwaltungsreglement oder Satzung sowie ggf. dem Basisinformationsblatt (PRIIP-KIDs), ergänzt durch den letzten geprüften Jahresbericht bzw. den letzten Halbjahresbericht zu entnehmen. Diese Unterlagen stellen die alleinverbindliche Grundlage für den Kauf von Fondsanteilen dar. Sie sind in elektronischer oder gedruckter Form in deutscher Sprache kostenlos bei der Axxion S.A., 15 rue de Flaxweiler, L-6776 Grevenmacher erhältlich.\n\nDer letzte Nettoinventarwert kann über die Internetseite der Axxion S.A. abgerufen werden.\n\nBitte beachten Sie: Wertentwicklungen in der Vergangenheit sind kein verlässlicher Indikator für die künftige Wertentwicklung. Investmentfonds unterliegen marktbedingten Kursschwankungen, die zu Verlusten, bis hin zum Totalverlust des eingesetzten Kapitals, führen können. Es wird ausdrücklich auf die Risikohinweise des Verkaufsprospektes verwiesen. Die zukünftige Wertentwicklung unterliegt der Besteuerung, die von der persönlichen Situation des jeweiligen Anlegers abhängig ist und sich in der Zukunft ändern kann.\n\nDie Bruttowertentwicklung (BVI-Methode) berücksichtigt bereits alle auf Fondsebene anfallenden Kosten und geht von einer Wiederanlage eventueller Ausschüttung aus. Sofern nicht anders angegeben, entsprechen alle dargestellten Wertentwicklungen der Bruttowertentwicklung. Die Nettowertentwicklung geht von einer Modellrechnung mit einem investierten Betrag von 1.000 €, dem max. Ausgabeaufschlag sowie einem Rücknahmeabschlag (falls vorgesehen, siehe Stammdaten) aus. Sie berücksichtigt keine anderen eventuell anfallenden individuellen Kosten des Anlegers, wie beispielsweise eine Depotführungsgebühr.\n\nDie ausgegebenen Anteile dieses Fonds dürfen nur in solchen Rechtsordnungen zum Kauf angeboten oder verkauft werden, in denen ein solches Angebot oder ein solcher Verkauf zulässig ist. So dürfen die Anteile dieses Fonds weder innerhalb der USA noch an oder für Rechnung von US Staatsbürgern oder in den USA ansässigen US-Personen zum Kauf angeboten oder an diese verkauft werden. Dieses Dokument und die in ihm enthaltenen Informationen dürfen nicht in den USA verbreitet werden. Die Verbreitung und Veröffentlichung dieses Dokumentes sowie das Angebot oder ein Verkauf der Anteile können auch in anderen Rechtsordnungen Beschränkungen unterworfen sein.',
  };
  // Helper function to split text intelligently
  const splitLegalNotesText = (text: string): string[] => {
    // First, try to split by paragraphs
    const paragraphs = text.split('\n\n').filter((p: string) => p.trim().length > 0);
    
    // If we have multiple paragraphs, use them
    if (paragraphs.length > 1) {
      return paragraphs;
    }
    
    // If it's one long paragraph, split by character count (approximately 2000 chars per part)
    // Try to split at sentence boundaries
    const maxCharsPerPart = 2000;
    const fullText = paragraphs[0] || text;
    
    if (fullText.length <= maxCharsPerPart) {
      return [fullText];
    }
    
    // Split into chunks, trying to break at sentence boundaries
    const chunks: string[] = [];
    let currentChunk = '';
    const sentences = fullText.split(/([.!?]\s+)/);
    
    for (let i = 0; i < sentences.length; i += 2) {
      const sentence = sentences[i] + (sentences[i + 1] || '');
      const potentialChunk = currentChunk + sentence;
      
      if (potentialChunk.length > maxCharsPerPart && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk = potentialChunk;
      }
    }
    
    if (currentChunk.trim().length > 0) {
      chunks.push(currentChunk.trim());
    }
    
    // If still too long, split by character count
    if (chunks.length === 0 || chunks[0].length > maxCharsPerPart * 1.5) {
      const charChunks: string[] = [];
      for (let i = 0; i < fullText.length; i += maxCharsPerPart) {
        let chunk = fullText.substring(i, i + maxCharsPerPart);
        // Try to break at word boundary
        if (i + maxCharsPerPart < fullText.length) {
          const lastSpace = chunk.lastIndexOf(' ');
          if (lastSpace > maxCharsPerPart * 0.8) {
            chunk = chunk.substring(0, lastSpace);
            i -= (maxCharsPerPart - lastSpace);
          }
        }
        charChunks.push(chunk.trim());
      }
      return charChunks;
    }
    
    return chunks.length > 0 ? chunks : [fullText];
  };

  const legalNotesParagraphs = splitLegalNotesText(legalNotesData.text);
  // Split legal notes: first part (approximately first 2000 chars or first 4 paragraphs) on first page, rest on second page
  const splitIndex = legalNotesParagraphs.length > 4 ? 4 : Math.ceil(legalNotesParagraphs.length / 2);
  const firstPartLegalNotes = legalNotesParagraphs.slice(0, splitIndex);
  const secondPartLegalNotes = legalNotesParagraphs.slice(splitIndex);

  // Rolling Performance Chart Options
  const isAutomated = rollingPerformance?.chartType === 'Automated';
  const chartCategories = isAutomated && automatedPeriodLabels.length > 0
    ? automatedPeriodLabels
    : rollingPerformance.periods.map(p => p.label);
  
  const rollingChartOptions: ApexOptions = useMemo(() => ({
    chart: {
      id: 'rolling-performance-chart',
      type: 'bar',
      height: 300,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '80%',
        barHeight: '100%',
        borderRadius: 12,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(2)}%`,
      offsetY: -16,
      style: {
        fontSize: '10px',
        fontWeight: 600,
        colors: ['#111827'],
      },
    },
    stroke: {
      show: true,
      width: 0,
    },
    xaxis: {
      categories: chartCategories,
      labels: {
        rotate: 0,
        rotateAlways: false,
        trim: false,
        hideOverlappingLabels: false,
        minHeight: 76,
        maxHeight: 120,
        offsetY: 4,
        formatter: (val: string) => formatBarChartPeriodLabel(String(val ?? '')),
        style: {
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      title: {
        text: 'in %',
      },
      labels: {
        formatter: (value) => `${value}%`,
      },
    },
    legend: {
      position: 'bottom',
      show: isAutomated ? rollingPerformance?.automationType === 'all' : true,
    },
    colors: isAutomated && rollingPerformance?.barColorSets?.length
      ? rollingPerformance.barColorSets
      : [...(document?.template?.colorSets.slice(0, 2).reverse() || []), '#003F7A', '#667a7a'],
    fill: {
      opacity: 1,
    },
  }), [chartCategories, isAutomated, rollingPerformance?.automationType, rollingPerformance?.barColorSets, document?.template?.colorSets, automatedPeriodLabels]);

  const rollingChartSeries = useMemo(() => {
    if (isAutomated && automatedPeriodsData.length > 0) {
      // Use automated data
      return automatedPeriodsData.map((series) => ({
        name: series.name || '',
        data: series.data.map((value: number, index: number) => ({
          x: chartCategories[index],
          y: value,
        })),
      }));
    }
    // Use manual data
    return [
      {
        name: 'Netto',
        data: rollingPerformance.periods.map((p, index) => ({
          x: p.label,
          y: p.netto,
          columnWidthOffset: -5, // Negative offset to create gap
        })),
      },
      {
        name: 'Brutto',
        data: rollingPerformance.periods.map((p, index) => ({
          x: p.label,
          y: p.brutto,
          columnWidthOffset: -5, // Positive offset to create gap
        })),
      },
    ];
  }, [isAutomated, automatedPeriodsData, chartCategories, rollingPerformance.periods, automatedPeriodLabels]);

  const captureAllChartsForExport = useCallback(async () => {
    await new Promise((r) => setTimeout(r, CHART_CAPTURE_RENDER_DELAY_MS));

    if (indexedChartData && indexedChartData.length > 0) {
      try {
        const res: any = await (ApexCharts as any).exec('indexed-chart', 'dataURI', { scale: 3 });
        onChart2ImageChange?.(res?.imgURI);
      } catch (e) {
        console.error('Error capturing indexed chart:', e);
      }
    }

    // Automated charts keep `periods` empty; their bars come from automatedPeriodLabels.
    const hasRollingBars = isAutomated
      ? automatedPeriodLabels.length > 0
      : Boolean(rollingPerformance.periods?.length);
    if (hasRollingBars) {
      try {
        await new Promise((r) => setTimeout(r, CHART_CAPTURE_RENDER_DELAY_MS));
        const res: any = await (ApexCharts as any).exec('rolling-performance-chart', 'dataURI', { scale: 3 });
        onChart3ImageChange?.(res?.imgURI);
      } catch (e) {
        console.error('Error capturing rolling performance chart:', e);
      }
    }

    if (sectorAllocation && sectorAllocation.length > 0) {
      try {
        const res: any = await (ApexCharts as any).exec('sector-allocation-chart', 'dataURI', { scale: 3 });
        onPieChart1ImageChange?.(res?.imgURI);
      } catch (e) {
        console.error('Error capturing sector pie chart:', e);
      }
    }

    if (currencyAllocation && currencyAllocation.length > 0) {
      try {
        const chartElement = window.document.getElementById('apexchartscurrency-allocation-chart');
        if (chartElement) {
          const canvas = await html2canvas(chartElement, {
            backgroundColor: 'transparent',
            scale: 3,
            logging: false,
          });
          onPieChart2ImageChange?.(canvas.toDataURL('image/png'));
        }
      } catch (e) {
        console.error('Error capturing currency pie chart:', e);
      }
    }

    if (assetAllocation && assetAllocation.length > 0) {
      try {
        const res: any = await (ApexCharts as any).exec('asset-allocation-chart', 'dataURI', { scale: 3 });
        onPieChart3ImageChange?.(res?.imgURI);
      } catch (e) {
        console.error('Error capturing asset pie chart:', e);
      }
    }
  }, [
    indexedChartData,
    isAutomated,
    automatedPeriodLabels,
    rollingPerformance.periods,
    sectorAllocation,
    currencyAllocation,
    assetAllocation,
    onChart2ImageChange,
    onChart3ImageChange,
    onPieChart1ImageChange,
    onPieChart2ImageChange,
    onPieChart3ImageChange,
  ]);

  useEffect(() => {
    if (!onRegisterChartCapture) return;
    onRegisterChartCapture(captureAllChartsForExport);
  }, [onRegisterChartCapture, captureAllChartsForExport]);

  // Handle preview started - capture all charts (legacy flow)
  useEffect(() => {
    if (isPreviewStarted) {
      (async () => {
        try {
          await captureAllChartsForExport();
        } finally {
          onResetPreviewStarted(false);
        }
      })();
    }
  }, [isPreviewStarted, captureAllChartsForExport, onResetPreviewStarted]);

  // Capture pie charts on data change - only when preview is started and data is available
  useEffect(() => {
    if (!isPreviewStarted) return;
    
    let cancelled = false;
    const capture = async () => {
      try {
        await new Promise((r) => setTimeout(r, 150));
        if (!cancelled && sectorAllocation && sectorAllocation.length > 0) {
          const res: any = await (ApexCharts as any).exec('sector-allocation-chart', 'dataURI', { scale: 3 });
          if (!cancelled) {
            onPieChart1ImageChange?.(res?.imgURI);
          }
        }
      } catch (e) {
        if (!cancelled) {
          onPieChart1ImageChange?.(undefined);
        }
      }
    };
    if (sectorAllocation && sectorAllocation.length > 0) {
      capture();
    }
    return () => { cancelled = true; };
  }, [isPreviewStarted, onPieChart1ImageChange, sectorAllocation]);

  useEffect(() => {
    if (!isPreviewStarted) return;
    
    let cancelled = false;
    const capture = async () => {
      try {
        await new Promise((r) => setTimeout(r, 150));
        if (!cancelled && currencyAllocation && currencyAllocation.length > 0) {
          const chartElement = window.document.getElementById('apexchartscurrency-allocation-chart');
          if (chartElement && !cancelled) {
            const canvas = await html2canvas(chartElement, {
              backgroundColor: 'transparent',
              scale: 3,
              logging: false,
            });
            const dataUrl = canvas.toDataURL('image/png');
            if (!cancelled) {
              onPieChart2ImageChange?.(dataUrl);
            }
          } else if (!cancelled) {
            onPieChart2ImageChange?.(undefined);
          }
        }
      } catch (e) {
        if (!cancelled) {
          onPieChart2ImageChange?.(undefined);
        }
      }
    };
    if (currencyAllocation && currencyAllocation.length > 0) {
      capture();
    }
    return () => { cancelled = true; };
  }, [isPreviewStarted, onPieChart2ImageChange, currencyAllocation]);

  useEffect(() => {
    if (!isPreviewStarted) return;
    
    let cancelled = false;
    const capture = async () => {
      try {
        await new Promise((r) => setTimeout(r, 150));
        if (!cancelled && assetAllocation && assetAllocation.length > 0) {
          const res: any = await (ApexCharts as any).exec('asset-allocation-chart', 'dataURI', { scale: 3 });
          if (!cancelled) {
            onPieChart3ImageChange?.(res?.imgURI);
          }
        }
      } catch (e) {
        if (!cancelled) {
          onPieChart3ImageChange?.(undefined);
        }
      }
    };
    if (assetAllocation && assetAllocation.length > 0) {
      capture();
    }
    return () => { cancelled = true; };
  }, [isPreviewStarted, onPieChart3ImageChange, assetAllocation]);

  return (
    <>
      <Box sx={styles.container}>
        {/* Header */}
        <Box
          sx={{
            ...styles.header,
            backgroundImage: `url(${document?.template?.defaultFields?.additional_fields?.banner_image})`,
          }}
        >
          <Box sx={styles.headerLeft}>
            <Box sx={styles.logoBox}>
              <img src={document?.template?.icon_url} alt="logo" />
            </Box>
          </Box>
          <Box sx={styles.headerRight}>
            <Box sx={styles.dateBadge}>
              <Typography sx={styles.dateBadgeText}>FACTSHEET {formattedDate}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={styles.mainContent}>
          <Box sx={styles.splitContent}>
            {/* Left Column - Main Content */}
            <Box sx={styles.leftColumn}>
              <Box>
                <Typography sx={styles.title}>
                  {effectiveTitle}
                </Typography>
                <Typography sx={styles.subtitle}>
                  {effectiveSubtitle}
                </Typography>
              </Box>

              <Box id="chart-image-section" sx={styles.card}> 
                <Box sx={styles.cardHeader}>
                  <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.chartImageTitle || 'Chart-Platzhalter'}</Typography>
                </Box>
                <Box sx={styles.cardBody}>
                  {/* Chart Image or Placeholder */}
                  {chartImageUrl ? (
                    <Box sx={styles.chartContainer}>
                      <img 
                        src={chartImageUrl} 
                        alt="Chart" 
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                      />
                    </Box>
                  ) : (
                    <Dropzone
                      onDrop={async (acceptedFiles) => {
                        if (acceptedFiles.length > 0 && onDataChange) {
                          try {
                            const res = await uploadImage(acceptedFiles[0]);
                            if (res?.status && res?.fileName) {
                              // Check if fileName is already a full URL
                              let imageUrl = res.fileName;
                              if (!res.fileName.startsWith('http://') && !res.fileName.startsWith('https://')) {
                                // If it's just the key, construct the full URL
                                imageUrl = `https://cixon-finance-docs.s3.amazonaws.com/${res.fileName}`;
                              }
                              onDataChange({ ...effectiveData, chartImageUrl: imageUrl });
                            }
                          } catch (error) {
                            console.error('Error uploading image:', error);
                            window.alert(uploadErrorMessage(error));
                          }
                        }
                      }}
                    >
                      {({ getRootProps, getInputProps, isDragActive }) => (
                        <Box
                          {...getRootProps()}
                          sx={{
                            ...styles.chartPlaceholder,
                            border: isDragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
                            backgroundColor: isDragActive ? '#f0f7ff' : '#fafafa',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#1976d2',
                              backgroundColor: '#f0f7ff',
                            },
                          }}
                        >
                          <input {...getInputProps()} accept="image/*" />
                          <Typography sx={styles.placeholderText}>
                            {isDragActive
                              ? 'Drop the image here...'
                              : lineChartData && lineChartData.length > 0
                              ? 'Vollflächiger Chart wird hier angezeigt'
                              : 'Drag & drop an image here, or click to select'}
                          </Typography>
                        </Box>
                      )}
                    </Dropzone>
                  )}
                </Box>

                {/* Fund Manager Section */}
                <Box id="fund-manager-section" sx={styles.fundManagerSection}>
                  <Box sx={styles.fundManagerContent}>
                    {document?.template?.defaultFields?.additional_fields?.man_image ? (
                      <Box
                        component="img"
                        src={document?.template?.defaultFields?.additional_fields?.man_image}
                        alt={fundManager.name}
                        sx={styles.fundManagerPhoto}
                      />
                    ) : (
                      <Box sx={styles.fundManagerPhotoPlaceholder} />
                    )}
                    <Box sx={styles.fundManagerInfo}>
                      <Typography sx={styles.fundManagerName}>{fundManager.name}</Typography>
                      <Typography sx={styles.fundManagerCompany}>{fundManager.company}</Typography>
                      <Typography sx={styles.fundManagerResearch}>{fundManager.research}</Typography>
                      <Typography sx={styles.fundManagerCompany2}>{fundManager.company2}</Typography>
                      <Typography sx={styles.fundManagerPhone}>Telefon: {fundManager.phone}</Typography>
                      {
                        fundManager.hedgingRatio && (
                          <Box sx={styles.hedgingButton}>
                            <Typography sx={styles.hedgingButtonText}>
                              Absicherungsquote aktuell: {fundManager.hedgingRatio}
                            </Typography>
                          </Box>    
                        )
                      }
                    </Box>
                  </Box>                 
                </Box>
              </Box>              
            </Box>

            {/* Right Column - Sidebar */}
            <Box sx={styles.rightColumn}>
              <Box sx={styles.sidebar}>
                {/* Stammdaten Section */}
                <Box id="stammdaten-section" sx={styles.sidebarSection}>
                  <Typography sx={styles.sidebarSectionTitle}>{sectionHeaders.stammdaten}</Typography>
                  <Box sx={styles.kvGroup}>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Fondskategorie:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'fondskategorie', 'Aktienfonds')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Auflagedatum:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'auflagedatum', '02.09.2024')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>WKN:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'wkn', 'A3EWBE')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>ISIN:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'isin', 'DE000A3EWBE8')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Erstausgabepreis:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'erstausgabepreis', '148,37 EUR')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Fondsvolumen:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'fondsvolumen', '9,96 Mio. EUR')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Kurs (NAV):</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'kursNav', '146,39 EUR')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Fondswährung:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'fondswaehrung', 'EUR')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Fondsdomizil:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'fondsdomizil', 'DE')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Geschäftsjahresende:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'geschaeftsjahresende', '31-Aug.')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Ertragsverwendung:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'ertragsverwendung', 'Thesaurierend')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Rechtsform:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'rechtsform', 'OGAW-Sondervermögen')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Risiko-/Ertragsprofil (SRI 1-7)*:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'risikoErtragsprofil', '4')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Sparplanfähig:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'sparplanfaehig', 'ja')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Mindestzeichnungbetrag:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('masterData', 'mindestzeichnungbetrag', '100.000 EUR')}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Gebühren Section */}
                <Box id="fees-section" sx={styles.sidebarSection}>
                  <Typography sx={styles.sidebarSectionTitle}>Gebühren</Typography>
                  <Box sx={styles.kvGroup}>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Ausgabeaufschlag:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('fees', 'ausgabeaufschlag', '0% (MAX 5%)')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>Laufende Kosten (TER):</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('fees', 'laufendeKostenTer', '1,94%')}</Typography>
                    </Box>
                    <Box sx={styles.kvRow}>
                      <Typography sx={styles.kvLabel}>davon Verwaltungsgebühr:</Typography>
                      <Typography sx={styles.kvValue}>{getDisplayValue('fees', 'davonVerwaltungsgebuehr', '1,23% (MAX 2%)')}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>  
          </Box>

          {/* Monthly Commentary — single card with full text */}
          {commentaryParagraphs.length > 0 && (
            <Box id="monthly-commentary-section" sx={styles.card}>
              <Box sx={styles.cardHeader}>
                <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.monthlyCommentary}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                <Typography sx={styles.commentaryText} component="div">
                  {commentaryParagraphs.map((paragraph: string, index: number) => (
                    <span key={index}>
                      {paragraph}
                      {index < commentaryParagraphs.length - 1 && <><br /><br /></>}
                    </span>
                  ))}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>        
      </Box>

      {/* Indexed chart + cumulative table */}
      <Box sx={styles.container}>
        <Box sx={styles.mainContent}>
          {/* Indexed Value Development Chart Section */}
          <Box id="indexed-chart-section" sx={styles.card}>
            <Box sx={styles.cardHeader}>
              <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.indexedChart}</Typography>
            </Box>
            <Box sx={styles.cardBody}>
              {performanceNotice ? (
                <Box sx={styles.noticeBox}>
                  <Typography sx={styles.noticeText}>{performanceNotice}</Typography>
                </Box>
              ) : indexedChartData && indexedChartData.length > 0 ? (
                <Box sx={styles.chartContainer}>
                  <ApexLineChart
                    id="indexed-chart"
                    data={lineChartData}
                    isSolActive={!!document?.template?.isins?.length}
                    color={document?.template?.color}
                    loading={false}
                    showToolbar={false}
                    fillType="solid"
                    solidFillOpacity={0}
                    height={300}
                    background="transparent"
                    seriesName={historyLegendLabel}
                    yAxisPrefix=""
                    yAxisFormatter={(value: number) => {
                      const rounded = Math.round(value);
                      const formatted = Number.isFinite(rounded) ? rounded.toLocaleString('de-DE') : String(value);
                      return `${formatted} ${historyCurrencySymbol || ''}`.trim();
                    }}
                  />
                </Box>
              ) : (
                <Box sx={styles.chartPlaceholder}>
                  <Typography sx={styles.placeholderText}>
                    Chart wird hier angezeigt
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Cumulative Value Development Table Section */}
          <Box id="cumulative-performance-section" sx={styles.card}>
            <Box sx={styles.cardHeader}>
              <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.cumulativePerformance}</Typography>
            </Box>
            <Box sx={styles.cardBody}>
              <Box sx={styles.performanceTable}>
                {/* Header Row */}
                <Box sx={{
                  ...styles.performanceTableHeader,
                  gridTemplateColumns: `repeat(${cumulativePerformanceData.labels.length}, 1fr)`
                }}>
                  {cumulativePerformanceData.labels.map((label, index) => (
                    <Box key={index} sx={styles.performanceTableHeaderCell}>{label}</Box>
                  ))}
                </Box>
                {/* Data Rows */}
                {cumulativePerformanceData.values.map((row, rowIndex) => (
                  <Box 
                    key={rowIndex} 
                    sx={{
                      ...styles.performanceTableRow,
                      gridTemplateColumns: `repeat(${cumulativePerformanceData.labels.length}, 1fr)`
                    }}
                  >
                    {row.map((cell: any, colIndex: number) => {
                      if (performanceNotice) {
                        return (
                          <Box key={colIndex} sx={styles.performanceTableCell}>–</Box>
                        );
                      }
                      const columnType = cumulativePerformanceData.columns?.[colIndex]?.type || 'text';
                      const isDynamic = cell?.isDynamic && cell?.fields?.length;
                      const cellKey = `cumulative-${rowIndex}-${colIndex}-${until || ''}`;
                      const fetchedValue = isDynamic ? cellDataCache.get(cellKey) : null;
                      
                      let displayValue = '';
                      if (isDynamic && fetchedValue !== undefined && fetchedValue !== null) {
                        // Use fetched value from GraphQL
                        displayValue = String(fetchedValue);
                      } else if (columnType === 'text') {
                        displayValue = typeof cell === 'string' ? cell : (cell?.text || '');
                      } else if (columnType === 'link') {
                        displayValue = cell?.linkText || '';
                      } else if (columnType === 'avatar') {
                        displayValue = typeof cell === 'string' ? cell : '';
                      }
                      
                      return (
                        <Box key={colIndex} sx={styles.performanceTableCell}>
                          {columnType === 'avatar' && displayValue ? (
                            <Box component="img" src={displayValue} sx={{ width: 32, height: 32, borderRadius: '50%' }} />
                          ) : columnType === 'link' && cell?.linkUrl ? (
                            <Box component="a" href={cell.linkUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                              {displayValue} {displayValue && !displayValue.includes('%') ? '%' : ''}
                            </Box>
                          ) : (
                            <>
                              {displayValue} {displayValue && !displayValue.includes('%') ? '%' : ''}
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
              {performanceNotice && (
                <Typography sx={styles.noticeInline}>{performanceNotice}</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Rolling Performance Chart Section */}
      <Box id="rolling-performance-section" sx={styles.container}>
        <Box sx={styles.mainContent}>
          <Box sx={styles.card}>
            <Box sx={styles.cardHeader}>
              <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.rollingPerformance}</Typography>
            </Box>
            <Box sx={styles.cardBody}>
              {performanceNotice ? (
                <Box sx={styles.noticeBox}>
                  <Typography sx={styles.noticeText}>{performanceNotice}</Typography>
                </Box>
              ) : (
                <Box sx={styles.chartContainer}>
                  <ReactApexChart
                    type="bar"
                    height={300}
                    options={rollingChartOptions}
                    series={rollingChartSeries}
                  />
                </Box>
              )}
              
              {/* Explanatory Text */}
              <Box id="explanatory-text-section" sx={styles.explanatoryText}>
                <Typography sx={styles.explanatoryTextTitle}>{sectionHeaders.explanatoryTextTitle || 'Erläuterungen zur Wertentwicklung'}</Typography>
                {performanceNotice && (
                  <Typography sx={styles.noticeInline}>{performanceNotice}</Typography>
                )}
                <Typography sx={styles.explanatoryTextContent}>
                  {effectiveData.explanatoryText || 'Die Bruttorendite (BVI-Methode) berücksichtigt alle auf Fondsebene anfallenden Kosten, die vergangene Wertentwicklung berücksichtigt den Ausgabeaufschlag. Weitere Kosten können auf individuell anfallen (z.B. Depotkosten). Modellrechnung (netto): Ein Anleger möchte für 1.000,00 EUR Anteile erwerben. Bei einem Ausgabeaufschlag von 5,00 % muss er insgesamt 1.052,63 EUR aufwenden. Die Depotkosten ergeben sich aus dem Preis- und Leistungsverzeichnis ihrer depotführenden Bank. Bitte beachten Sie, dass der Referenzindex nur informatorischen Charakter hat und begrenzt keine Verpflichtung des Fondsmanagers, den Index oder dessen Wertentwicklung nachzubilden oder zu erreichende Mindestrendite darstellt. Quelle: FWW FundDataWeb, Stand: 29.08.2025. Bei unvollständiger Wertentwicklung.'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Investment Strategy and Awards Section */}
      <Box sx={styles.container}>
        <Box sx={styles.mainContent}>
          <Box sx={styles.twoColumnCards}>
            {/* Investment Strategy Card */}
            <Box id="investment-strategy-section" sx={styles.strategyCard}>
              <Box sx={styles.cardHeader}>
                <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.investmentStrategy}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                <Typography sx={styles.strategyText}>
                  {investmentStrategy}
                </Typography>
              </Box>
            </Box>

            {/* Awards Card */}
            <Box id="awards-section" sx={styles.awardsCard}>
              <Box sx={styles.cardHeader}>
                <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.awards}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                {awardsImageUrl ? (
                  <Box sx={styles.awardPlaceholderContainer}>
                    <img 
                      src={awardsImageUrl} 
                      alt="Awards" 
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                    />
                  </Box>
                ) : (
                  <Dropzone
                    onDrop={async (acceptedFiles) => {
                      if (acceptedFiles.length > 0 && onDataChange) {
                        try {
                          const res = await uploadImage(acceptedFiles[0]);
                          if (res?.status && res?.fileName) {
                            // Check if fileName is already a full URL
                            let imageUrl = res.fileName;
                            if (!res.fileName.startsWith('http://') && !res.fileName.startsWith('https://')) {
                              // If it's just the key, construct the full URL
                              imageUrl = `https://cixon-finance-docs.s3.amazonaws.com/${res.fileName}`;
                            }
                            onDataChange({ ...effectiveData, awardsImageUrl: imageUrl });
                          }
                        } catch (error) {
                          console.error('Error uploading image:', error);
                          window.alert(uploadErrorMessage(error));
                        }
                      }
                    }}
                  >
                    {({ getRootProps, getInputProps, isDragActive }) => (
                      <Box
                        {...getRootProps()}
                        sx={{
                          ...styles.awardPlaceholderContainer,
                          border: isDragActive ? '2px dashed #1976d2' : 'none',
                          backgroundColor: isDragActive ? '#f0f7ff' : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            border: '2px dashed #1976d2',
                            backgroundColor: '#f0f7ff',
                          },
                        }}
                      >
                        <input {...getInputProps()} accept="image/*" />
                        <Box sx={styles.awardPlaceholder}>
                          <Typography sx={styles.awardPlaceholderText}>
                            {isDragActive ? 'Drop image here...' : 'Award'}
                          </Typography>
                        </Box>
                        <Typography sx={styles.awardPlaceholderLabel}>
                          {isDragActive 
                            ? 'Drop the image here...' 
                            : 'Platzhalter für Fonds-Auszeichnungen'}
                        </Typography>
                      </Box>
                    )}
                  </Dropzone>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Four Cards Section - Top 10 Stocks, Sector, Currency, Asset Allocation */}
      <Box sx={styles.container}>
        <Box sx={styles.mainContent}>
          <Box sx={styles.fourCardGrid}>
            {/* Top 10 Stock Positions Card */}
            <Box id="top10-stocks-section" sx={styles.allocationCard}>
              <Box sx={styles.cardHeader}>
                <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.top10Stocks}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                <Box sx={styles.stockList}>
                  {top10Stocks.map((stock, index) => (
                    <Box key={index} sx={styles.stockListItem}>
                      <Typography sx={styles.stockListNumber}>{index + 1}.</Typography>
                      <Typography sx={styles.stockListName}>{stock.name}</Typography>
                      <Typography sx={styles.stockListPercentage}>{formatStockPercentage(stock.percentage)}%</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Sector Allocation Card */}
            <Box id="sector-allocation-section" sx={styles.allocationCard}>
              <Box sx={styles.cardHeader}>
                <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.sectorAllocation}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                {sectorAllocation && sectorAllocation.length > 0 ? (
                  <ApexPieChart
                    id="sector-allocation-chart"
                    data={sectorAllocation}
                    height={250}
                    background="transparent"
                    colors={colorSets.length > 0 ? colorSets : defaultColors}
                    additionalOptions={{
                      stroke: { width: 3, colors: ['#fff'] },
                    }}
                  />
                ) : (
                  <Box sx={styles.chartPlaceholder}>
                    <Typography sx={styles.placeholderText}>Keine Daten</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Currency Allocation Card */}
            <Box id="currency-allocation-section" sx={styles.allocationCard}>
              <Box sx={styles.cardHeader}>
                <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.currencyAllocation}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                {currencyAllocation && currencyAllocation.length > 0 ? (
                  <ApexPieChart
                    id="currency-allocation-chart"
                    data={currencyAllocation}
                    height={250}
                    background="transparent"
                    colors={colorSets.length > 0 ? colorSets : defaultColors}
                    additionalOptions={{
                      stroke: { width: 3, colors: ['#fff'] },
                    }}
                  />
                ) : (
                  <Box sx={styles.chartPlaceholder}>
                    <Typography sx={styles.placeholderText}>Keine Daten</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Asset Allocation Card */}
            <Box id="asset-allocation-section" sx={styles.allocationCard}>
              <Box sx={styles.cardHeader}>
                <Typography sx={styles.cardHeaderTitle}>{sectionHeaders.assetAllocation}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                <ReactApexChart
                  key={JSON.stringify(assetAllocation)}
                  type="donut"
                  height={200}
                  options={{
                    chart: {
                      id: 'asset-allocation-chart',
                      type: 'donut',
                      background: 'transparent',
                      toolbar: { show: false },
                    },
                    labels: assetAllocation.map((a) => a.label),
                    legend: { show: true, position: 'right' },
                    dataLabels: {
                      enabled: true,
                      formatter: (val: number) => `${Math.round(val)}%`,
                      style: { fontWeight: 700 },
                    },
                    colors: colorSets.length > 0 ? colorSets : ['#1c4f92', '#62418b', '#853874', '#9d345c'],
                    stroke: { width: 3, colors: ['#fff'] },
                    plotOptions: {
                      pie: {
                        donut: {
                          size: '40%',
                        },
                      }
                    },
                  }}
                  series={assetAllocation.map((a) => a.value)}
                />
              </Box>
            </Box>
          </Box>

          {/* Three Panels Section - Target Group, Investment Horizon, Risk Indicator */}
          <Box id="target-group-risk-section" sx={styles.threePanelGrid}>
            {/* Target Group Panel */}
            <Box sx={styles.infoPanel}>
              <Box sx={styles.infoPanelHeader}>
                <Typography sx={styles.infoPanelTitle}>ZIELGRUPPE</Typography>
              </Box>
              <Box sx={styles.infoPanelBody}>
                <Typography sx={styles.infoPanelText}>
                  {targetGroup}
                </Typography>
              </Box>
            </Box>

            {/* Investment Horizon Panel */}
            <Box sx={styles.infoPanel}>
              <Box sx={styles.infoPanelHeader}>
                <Typography sx={styles.infoPanelTitle}>ANLAGEHORIZONT</Typography>
              </Box>
              <Box sx={styles.infoPanelBody}>
                <Typography sx={styles.infoPanelText}>
                  {investmentHorizon}
                </Typography>
              </Box>
            </Box>

            {/* Risk Indicator Panel */}
            <Box sx={styles.infoPanel}>
              <Box sx={styles.infoPanelHeader}>
                <Typography sx={styles.infoPanelTitle}>RISIKOINDIKATOR</Typography>
              </Box>
              <Box sx={styles.infoPanelBody}>
                {/* Risk Scale Labels */}
                <Box sx={styles.riskScaleLabels}>
                  <Typography sx={styles.riskScaleLabel}>Geringeres Risiko</Typography>
                  <Typography sx={styles.riskScaleLabel}>Höheres Risiko</Typography>
                </Box>

                {/* Risk Scale Boxes */}
                <Box sx={styles.riskScaleBoxes}>
                  {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                    <Box
                      key={num}
                      sx={{
                        ...styles.riskScaleBox,
                        ...(num === riskIndicator ? styles.riskScaleBoxActive : {}),
                      }}
                    >
                      <Typography
                        sx={{
                          ...styles.riskScaleBoxNumber,
                          ...(num === riskIndicator ? styles.riskScaleBoxNumberActive : {}),
                        }}
                      >
                        {num}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Gradient Bar */}
                <Box sx={styles.riskGradientBar}>
                  <Box sx={styles.riskGradientBarInner} />
                  <Box sx={styles.riskGradientArrow}>→</Box>
                </Box>

                {/* Return Indicator Labels */}
                <Box sx={styles.riskScaleLabels}>
                  <Typography sx={styles.riskScaleLabel}>Potentiell geringerer Ertrag</Typography>
                  <Typography sx={styles.riskScaleLabel}>Potentiell höherer Ertrag</Typography>
                </Box>

                {/* Disclaimer Text */}
                <Typography sx={styles.riskDisclaimerText}>
                  Die zur Berechnung des synthetischen Indikators verwendeten historischen Daten sind kein verlässlicher Hinweis auf das künftige Risiko- und Ertragsprofil der Anteilsklasse. Die periodische Einstufung kann Änderungen unterliegen und sich im Laufe der Zeit daher verändern.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Opportunities and Risks Section */}
          <Box sx={styles.twoColumn}>
            {/* Opportunities Card */}
            <Box id="opportunities-section" sx={styles.card}>
              <Box sx={[styles.cardHeader, styles.blueCardHeader]}>
                <Typography sx={[styles.cardHeaderTitle, styles.colorWhite]}>{sectionHeaders.opportunities}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                {opportunities.map((item: string, index: number) => (
                  <Box key={index} sx={styles.bulletItem}>
                    <Typography sx={styles.bulletPoint}>+</Typography>
                    <Typography sx={styles.bulletText}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Risks Card */}
            <Box id="risks-section" sx={styles.card}>
              <Box sx={[styles.cardHeader, styles.blueCardHeader]}>
                <Typography sx={[styles.cardHeaderTitle, styles.colorWhite]}>{sectionHeaders.risks}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                {risks.map((item: string, index: number) => (
                  <Box key={index} sx={styles.bulletItem}>
                    <Typography sx={styles.bulletPoint}>-</Typography>
                    <Typography sx={styles.bulletText}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Sales Prospectus Section */}
          <Box id="sales-prospectus-section" sx={styles.card}>
            <Box sx={[styles.cardHeader, styles.blueCardHeader]}>
              <Typography sx={[styles.cardHeaderTitle, styles.colorWhite]}>{sectionHeaders.salesProspectus}</Typography>
            </Box>
            <Box sx={styles.cardBody}>
              <Typography sx={styles.cardBodyText}>{salesProspectus}</Typography>
            </Box>
          </Box>

          {/* Legal Notes Section - First Part */}
          <Box id="legal-notes-section" sx={styles.card}>
            <Box sx={[styles.cardHeader, styles.blueCardHeader]}>
              <Typography sx={[styles.cardHeaderTitle, styles.colorWhite]}>{sectionHeaders.legalNotes}</Typography>
            </Box>
            <Box sx={styles.cardBody}>
              {/* Key-Value Pairs */}
              <Box sx={styles.legalKvRow}>
                <Typography sx={styles.legalKvLabel}>Verwahrstelle:</Typography>
                <Typography sx={styles.legalKvValue}>{legalNotesData.verwahrstelle}</Typography>
              </Box>
              <Box sx={styles.legalKvRow}>
                <Typography sx={styles.legalKvLabel}>Verwaltungsgesellschaft:</Typography>
                <Typography sx={styles.legalKvValue}>{legalNotesData.verwaltungsgesellschaft}</Typography>
              </Box>
              
              {/* Legal Text - First Part */}
              {firstPartLegalNotes.map((paragraph: string, index: number) => (
                <Typography key={index} sx={styles.legalText}>
                  {paragraph}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Legal Notes Section - Second Part (if exists) */}
      {secondPartLegalNotes.length > 0 && (
        <Box sx={styles.container}>
          <Box sx={styles.mainContent}>
            <Box sx={styles.card}>
              <Box sx={[styles.cardHeader, styles.blueCardHeader]}>
                <Typography sx={[styles.cardHeaderTitle, styles.colorWhite]}>{sectionHeaders.legalNotes}</Typography>
              </Box>
              <Box sx={styles.cardBody}>
                {/* Separator Line */}
                <Box sx={styles.legalSeparator} />
                
                {/* Legal Text - Second Part */}
                {secondPartLegalNotes.map((paragraph: string, index: number) => (
                  <Typography key={index} sx={styles.legalText}>
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default FactsheetPreview;
